/**
 * Shared helpers for the Activity timeline (project detail + recipient panels).
 *
 * The feed merges two record kinds — activities (`start_time`) and tasks
 * (`due_date`) — so every date-related decision goes through `eventDateOf` to
 * keep both kinds on one axis.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const ACTIVITY_FILTERS = [
  { value: 'all',     label: 'All' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'call',    label: 'Call' },
  { value: 'email',   label: 'Email' },
  { value: 'sms',     label: 'SMS' },
  { value: 'task',    label: 'Task' },
  { value: 'other',   label: 'Other' },
];

export const isDone = item => item.status === 'done' || item.status === 'cancelled';

export const isTaskItem = item => item._type === 'task';

/**
 * Parse an API date. `due_date` arrives as a bare `YYYY-MM-DD`, which `Date`
 * would read as UTC midnight and shift a day in western timezones — build those
 * in local time instead. Returns null for blank/unparseable input.
 */
export const parseDate = (value) => {
  if (!value) return null;
  const raw   = String(value);
  const match = raw.match(DATE_ONLY);
  const date  = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The date an item belongs to on the timeline; falls back to creation date. */
export const eventDateOf = item =>
  parseDate(isTaskItem(item) ? item.due_date : item.start_time)
  || parseDate(item.created_at);

/** Newest first. Items without any usable date sink to the bottom. */
export const sortByEventDesc = items =>
  [...items].sort((a, b) => {
    const da = eventDateOf(a);
    const db = eventDateOf(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db - da;
  });

/**
 * Split into the two sections the feed renders: still-open items on top,
 * completed/cancelled ones under an "Activity history" heading. Both are sorted
 * newest first.
 */
export const splitByStatus = (items) => {
  const open    = sortByEventDesc(items.filter(i => !isDone(i)));
  const history = sortByEventDesc(items.filter(isDone));
  return { open, history };
};

// ── Formatting ───────────────────────────────────────────────────────────────

const pad = n => String(n).padStart(2, '0');

export const fmtDate = (value) => {
  const d = parseDate(value);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : null;
};

export const fmtTime = (value) => {
  const d = parseDate(value);
  return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : null;
};

export const fmtDateTime = (value) => {
  const d = parseDate(value);
  return d ? `${fmtDate(d)} at ${fmtTime(d)}` : '—';
};

/** Short weekday used as a secondary line in the date gutter, e.g. "Thu". */
export const fmtWeekday = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short' }) : null;
};

/** The scheduled window of an item, e.g. "17:00 – 17:30" or "09:30". */
export const fmtEventTime = (item) => {
  if (isTaskItem(item)) return item.due_time || null;
  if (!item.start_time) return null;
  const start = fmtTime(item.start_time);
  const end   = item.end_time ? fmtTime(item.end_time) : null;
  return end ? `${start} – ${end}` : start;
};

/**
 * Compact "how late is it" label for the overdue pill — the exact date already
 * sits in the gutter, so repeating it there would be noise.
 */
export const fmtOverdueAge = (item) => {
  const due = eventDateOf(item);
  if (!due) return 'Overdue';
  const days = Math.floor((Date.now() - due.getTime()) / 86400000);
  if (days <= 0)  return 'Overdue';
  if (days === 1) return 'Overdue 1d';
  if (days < 30)  return `Overdue ${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Overdue ${months}mo`;
  return `Overdue ${Math.floor(months / 12)}y`;
};

/** True when two items fall on the same calendar day (drives the date gutter). */
export const isSameDay = (a, b) => {
  const da = eventDateOf(a);
  const db = eventDateOf(b);
  if (!da || !db) return false;
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
};
